import { User } from "firebase/auth";
import { apiFetch } from "./client";
import { Club } from "./clubs";
import { LeagueMode, LeagueStatus } from "./leagues";

// ─── Competitions ─────────────────────────────────────────────────────────────

export type CompetitionClub = Club & { externalTeamId: string | null };

export interface AdminCompetition {
  id: string;
  name: string;
  season: string;
  type: "CLUB" | "INTERNATIONAL";
  supportedModes: string[];
  /** Per-mode last-joinable-GW cap. Missing/null value for a mode = no cap. */
  modeJoinCaps: Record<string, number | null>;
  status: "UPCOMING" | "ACTIVE" | "COMPLETED";
  archived: boolean;
  config: {
    topTierSize: number | null;
    relegationSize: number | null;
    championEnabled: boolean;
    topTierLabel: string | null;
    relegationLabel: string | null;
  } | null;
  gameweeks: {
    id: string;
    number: number;
    name: string;
    shortName: string | null;
    completedAt: string | null;
  }[];
  clubs: CompetitionClub[];
  /** Cheap per-mode prerequisite signals for the modes screen's "Needs setup" chips. */
  hasActiveSquadPlayers: boolean;
  hasFixtures: boolean;
  syndicatePotCount: number;
}

/**
 * Shape returned by GET /admin/competitions (list) — no nested clubs, and no
 * modeJoinCaps/config either: the list endpoint never sends those (only the
 * detail endpoint does), so they're narrowed out here rather than left as a
 * type promise the response doesn't keep.
 */
export type AdminCompetitionListItem = Omit<AdminCompetition, "clubs" | "modeJoinCaps" | "config" | "hasActiveSquadPlayers" | "hasFixtures" | "syndicatePotCount">;

export interface AdminCompetitionsPage {
  competitions: AdminCompetitionListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CreateCompetitionPayload {
  name: string;
  season: string;
  type: "CLUB" | "INTERNATIONAL";
  supportedModes: string[];
  gameweekCount: number;
  gameweekNames?: Record<number, string>;
  gameweekShortNames?: Record<number, string>;
  clubIds?: string[];
}

// ─── Capabilities ─────────────────────────────────────────────────────────────

export interface AdminCapabilities {
  capabilities: string[];
  competitionIds: string[] | "ALL";
}

export function fetchAdminCapabilities(user: User): Promise<AdminCapabilities> {
  return apiFetch(user, "/admin/me/capabilities");
}

export function fetchAdminCompetitions(user: User, params?: { search?: string; page?: number }): Promise<AdminCompetitionsPage> {
  const q = new URLSearchParams();
  if (params?.search) q.set("search", params.search);
  if (params?.page) q.set("page", String(params.page));
  return apiFetch(user, `/admin/competitions?${q}`);
}

export function fetchAdminCompetition(user: User, id: string): Promise<AdminCompetition> {
  return apiFetch(user, `/admin/competitions/${id}`);
}

export function createCompetition(user: User, payload: CreateCompetitionPayload): Promise<AdminCompetition> {
  return apiFetch(user, "/admin/competitions", { method: "POST", body: JSON.stringify(payload) });
}

export function renameCompetition(
  user: User,
  id: string,
  name: string,
  supportedModes?: string[],
  modeJoinCaps?: Record<string, number | null>,
): Promise<AdminCompetition> {
  return apiFetch(user, `/admin/competitions/${id}`, {
    method: "PATCH",
    body: JSON.stringify({
      name,
      ...(supportedModes ? { supportedModes } : {}),
      ...(modeJoinCaps !== undefined ? { modeJoinCaps } : {}),
    }),
  });
}

export function deleteCompetition(user: User, id: string, deleteLeagues?: boolean): Promise<void> {
  const q = deleteLeagues ? "?deleteLeagues=true" : "";
  return apiFetch(user, `/admin/competitions/${id}${q}`, { method: "DELETE" });
}

export function startCompetition(user: User, id: string): Promise<{ status: string }> {
  return apiFetch(user, `/admin/competitions/${id}/start`, { method: "POST" });
}

export function archiveCompetition(user: User, id: string): Promise<{ archived: boolean }> {
  return apiFetch(user, `/admin/competitions/${id}/archive`, { method: "POST" });
}

export function unarchiveCompetition(user: User, id: string): Promise<{ archived: boolean }> {
  return apiFetch(user, `/admin/competitions/${id}/unarchive`, { method: "POST" });
}

export function addGameweeks(user: User, id: string, count: number): Promise<{ added: number; firstNumber: number; lastNumber: number }> {
  return apiFetch(user, `/admin/competitions/${id}/gameweeks`, { method: "POST", body: JSON.stringify({ count }) });
}

export function deleteGameweek(user: User, competitionId: string, gwId: string): Promise<void> {
  return apiFetch(user, `/admin/competitions/${competitionId}/gameweeks/${gwId}`, { method: "DELETE" });
}

export function updateClubExternalTeamId(user: User, competitionId: string, clubId: string, externalTeamId: string | null): Promise<CompetitionClub> {
  return apiFetch(user, `/admin/competitions/${competitionId}/clubs/${clubId}`, {
    method: "PATCH",
    body: JSON.stringify({ externalTeamId }),
  });
}

// ─── FP Scoring Config ─────────────────────────────────────────────────────────

export interface FPScoringConfig {
  competitionId: string;
  topTierSize: number | null;
  relegationSize: number | null;
  championEnabled: boolean;
  topTierLabel: string | null;
  relegationLabel: string | null;
}

export function updateFPConfig(
  user: User,
  competitionId: string,
  data: {
    topTierSize?: number | null;
    relegationSize?: number | null;
    championEnabled?: boolean;
    topTierLabel?: string | null;
    relegationLabel?: string | null;
  },
): Promise<FPScoringConfig> {
  return apiFetch(user, `/admin/competitions/${competitionId}/fp-config`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// ─── FPL Automation ────────────────────────────────────────────────────────────

export interface CompetitionAutomationConfig {
  competitionId: string;
  sourceType: "FPL" | "MANUAL";
  enabled: boolean;
  autoSubmit: boolean;
  lastSyncAt: string | null;
  lastSyncStats: { created: number; updated: number; skipped: number; errors: string[] } | null;
  // FP scoring fields (included in the same config row)
  topTierSize: number | null;
  relegationSize: number | null;
  championEnabled: boolean;
}

export function fetchAutomationConfig(user: User, competitionId: string): Promise<CompetitionAutomationConfig> {
  return apiFetch(user, `/admin/competitions/${competitionId}/automation`);
}

export function updateAutomationConfig(
  user: User,
  competitionId: string,
  data: { enabled?: boolean; autoSubmit?: boolean; sourceType?: "FPL" | "MANUAL" },
): Promise<CompetitionAutomationConfig> {
  return apiFetch(user, `/admin/competitions/${competitionId}/automation`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteAutomationConfig(user: User, competitionId: string): Promise<void> {
  return apiFetch(user, `/admin/competitions/${competitionId}/automation`, { method: "DELETE" });
}

export interface FplSyncState {
  id: string;
  key: string;
  lastFetchedAt: string;
  lastSuccessAt: string | null;
  consecutiveFailures: number;
  lastError: string | null;
  lastEntryEvent: number | null;
}

export interface FplSyncStatusResponse {
  items: FplSyncState[];
  total: number;
  page: number;
  limit: number;
  automationEnabled: boolean;
  unconfirmedReadyProposals: number;
}

export function fetchFplSyncStatus(user: User, page = 1, limit = 20): Promise<FplSyncStatusResponse> {
  return apiFetch(user, `/admin/fpl-sync-status?page=${page}&limit=${limit}`);
}

export interface FplPingResult {
  ok: boolean;
  playerCount?: number;
  teamCount?: number;
  gameweekCount?: number;
  currentGameweek?: number | null;
  error?: string;
}

export function pingFplApi(user: User): Promise<FplPingResult> {
  return apiFetch(user, "/admin/fpl-ping");
}

export interface FplPollResult {
  ok: boolean;
  lastFetchedAt: string | null;
  lastError: string | null;
  error?: string;
}

// Production-reachable on-demand poll trigger — pulls the hourly cron's cycle forward rather
// than waiting out the lag. POST /dev/fpl-poll is local-only and unauthenticated; this is the
// admin-gated equivalent for prod.
export function runFplPollNow(user: User): Promise<FplPollResult> {
  return apiFetch(user, "/admin/fpl-poll", { method: "POST" });
}

export interface TestEmailResult {
  ok: boolean;
  message?: string;
  error?: string;
}

// Production-reachable equivalent of POST /dev/test-email (local-only) — sends a dummy
// auto-submit alert/success email to OPS_ALERT_EMAIL, to check the Resend wiring without
// waiting for a real hold or completion.
export function runTestEmail(user: User, kind: "alert" | "success" = "alert"): Promise<TestEmailResult> {
  return apiFetch(user, `/admin/test-email?kind=${kind}`, { method: "POST" });
}

export interface AttentionItem {
  severity: "info" | "warning" | "error";
  kind: string;
  message: string;
  href: string;
}

export function fetchAdminAttention(user: User): Promise<{ items: AttentionItem[] }> {
  return apiFetch(user, "/admin/attention");
}

export interface AutoSubmitReason {
  code: string;
  detail: string;
  message: string;
}

export interface AutoSubmitReadiness {
  eligible: boolean;
  provisional?: boolean;
  blocks: AutoSubmitReason[];
  warnings: AutoSubmitReason[];
  evaluatedAt: string;
  lastError: string | null;
}

export type GameweekFetchProposal =
  | {
      id: string;
      gameweekId: string;
      status: "READY";
      payload: {
        // yellowCards/redCards absent on proposals fetched before card ingestion shipped —
        // treat as 0 for those. Enforced in GameweekOpsPage's applyProposalToForm, which
        // preserves an existing row's card value rather than overwriting it with this 0.
        stats: { playerId: string; playerName: string; goals: number; assists: number; yellowCards?: number; redCards?: number }[];
        goalsThisRound: number;
        // Absent on proposals staged before this field shipped, and on proposals with no
        // associated FPL event at all — treat as "not confirmed" (false) in either case, never
        // as "confirmed" by default.
        dataChecked?: boolean;
        // Absent on proposals staged before this field shipped. When present and matched is 0
        // despite eventRows > 0, every scorer this round failed to resolve to a player/club in
        // this competition — an empty stats array here means broken FPL mapping, not a quiet
        // gameweek. See docs on the Data Source page's club/player mapping.
        coverage?: { eventRows: number; matched: number; unknownElement: number; unmatchedClub: number; unmatchedPlayer: number };
        // Absent on a proposal staged before this shipped. Readiness is recorded on every
        // READY stage regardless of whether autoSubmit is even turned on — with it off, the
        // only block is AUTO_SUBMIT_OFF, so this panel doubles as a free dry run.
        autoSubmit?: AutoSubmitReadiness;
      };
      fplEvent: number;
      errorMessage: string | null;
      fetchedAt: string;
      confirmedAt: string | null;
    }
  | {
      id: string;
      gameweekId: string;
      status: "INCOMPLETE" | "FAILED";
      payload: unknown;
      fplEvent: number;
      errorMessage: string | null;
      fetchedAt: string;
      confirmedAt: string | null;
    };

export function fetchGameweekProposal(user: User, competitionId: string, gwId: string): Promise<GameweekFetchProposal | null> {
  return apiFetch(user, `/admin/competitions/${competitionId}/gameweeks/${gwId}/proposal`);
}

export function fetchGameweekFromFpl(user: User, competitionId: string, gwId: string): Promise<GameweekFetchProposal> {
  return apiFetch(user, `/admin/competitions/${competitionId}/gameweeks/${gwId}/fetch`, { method: "POST" });
}

export interface FplPreviewTeam {
  externalTeamId: number;
  fplName: string;
  fplShortName: string;
  suggestedClubId: string | null;
  suggestedClubName: string | null;
}

export interface FplPreview {
  teams: FplPreviewTeam[];
  playerCount: number;
  gameweekCount: number;
}

export interface FplImportResult {
  clubsLinked: number;
  playersSynced: number;
  gameweeksSynced: number;
}

export function fetchFplPreview(user: User, competitionId: string): Promise<FplPreview> {
  return apiFetch(user, `/admin/competitions/${competitionId}/fpl-preview`);
}

export function importFplData(
  user: User,
  competitionId: string,
  clubMappings: { externalTeamId: number; clubId: string }[],
): Promise<FplImportResult> {
  return apiFetch(user, `/admin/competitions/${competitionId}/import-fpl`, {
    method: "POST",
    body: JSON.stringify({ clubMappings }),
  });
}

export function fetchCompetitionClubs(user: User, competitionId: string): Promise<Club[]> {
  return apiFetch(user, `/admin/competitions/${competitionId}/clubs`);
}

export function linkClubsToCompetition(
  user: User,
  competitionId: string,
  clubIds: string[],
): Promise<{ linked: number }> {
  return apiFetch(user, `/admin/competitions/${competitionId}/clubs`, {
    method: "POST",
    body: JSON.stringify({ clubIds }),
  });
}

export function unlinkClubFromCompetition(user: User, competitionId: string, clubId: string): Promise<void> {
  return apiFetch(user, `/admin/competitions/${competitionId}/clubs/${clubId}`, { method: "DELETE" });
}

// ─── Delegate grants ────────────────────────────────────────────────────────────
// Per-competition CompetitionGrant CRUD — only "gameweek:submit" is ever granted today.

export interface CompetitionGrant {
  id: string;
  capability: string;
  createdAt: string;
  user: { id: string; displayName: string; email: string };
}

export function fetchCompetitionGrants(user: User, competitionId: string): Promise<CompetitionGrant[]> {
  return apiFetch(user, `/admin/competitions/${competitionId}/grants`);
}

export function createCompetitionGrant(user: User, competitionId: string, email: string): Promise<CompetitionGrant> {
  return apiFetch(user, `/admin/competitions/${competitionId}/grants`, {
    method: "POST",
    body: JSON.stringify({ email, capability: "gameweek:submit" }),
  });
}

export function deleteCompetitionGrant(user: User, competitionId: string, grantId: string): Promise<void> {
  return apiFetch(user, `/admin/competitions/${competitionId}/grants/${grantId}`, { method: "DELETE" });
}

export interface MyGameweekItem {
  competitionId: string;
  competitionName: string;
  gameweekId: string;
  gameweekNumber: number;
  gameweekName: string;
  completedAt: string | null;
}

export function fetchMyGameweeks(user: User): Promise<{ items: MyGameweekItem[] }> {
  return apiFetch(user, "/admin/me/gameweeks");
}

// ─── Gameweeks ────────────────────────────────────────────────────────────────

export interface PlayerStatInput {
  playerId: string;
  goals: number;
  assists: number;
  yellowCards: number;
  redCards: number;
}

export interface StandingInput {
  clubId: string;
  position: number;
}

export function fetchGameweekStats(
  user: User,
  competitionId: string,
  gwId: string,
): Promise<{
  stats: { playerId: string; playerName: string; goals: number; assists: number; yellowCards: number; redCards: number }[];
  standings: { clubId: string; position: number }[];
  goalsThisRound: number | null;
}> {
  return apiFetch(user, `/admin/competitions/${competitionId}/gameweeks/${gwId}/stats`);
}

export function submitGameweek(
  user: User,
  competitionId: string,
  gwId: string,
  stats: PlayerStatInput[],
  standings?: StandingInput[],
  goalsThisRound?: number,
  markComplete?: boolean,
): Promise<{ status: string }> {
  return apiFetch(user, `/admin/competitions/${competitionId}/gameweeks/${gwId}/submit`, {
    method: "POST",
    body: JSON.stringify({
      stats,
      ...(standings ? { standings } : {}),
      ...(typeof goalsThisRound === "number" ? { goalsThisRound } : {}),
      ...(typeof markComplete === "boolean" ? { markComplete } : {}),
    }),
  });
}

export function patchGameweek(
  user: User,
  competitionId: string,
  gwId: string,
  data: { name?: string; shortName?: string | null },
): Promise<{ id: string; name: string; shortName: string | null }> {
  return apiFetch(user, `/admin/competitions/${competitionId}/gameweeks/${gwId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function reopenGameweek(user: User, competitionId: string, gwId: string): Promise<{ reopened: boolean }> {
  return apiFetch(user, `/admin/competitions/${competitionId}/gameweeks/${gwId}/reopen`, { method: "POST" });
}

export interface GameweekOpsCompetitionClub {
  id: string;
  name: string;
  shortName: string | null;
  externalTeamId: string | null;
}

export interface GameweekOpsData {
  gameweek: {
    id: string;
    number: number;
    name: string;
    shortName: string | null;
    completedAt: string | null;
    goalsThisRound: number | null;
    // Auto-completed before FPL confirmed the round; the poll keeps re-checking it until FPL does.
    fplProvisional: boolean;
  };
  competition: {
    id: string;
    name: string;
    supportedModes: string[];
    sourceType: "FPL" | "MANUAL";
    clubs: GameweekOpsCompetitionClub[];
  };
  fixtures: AdminFixture[];
  existing: {
    stats: { playerId: string; playerName: string; goals: number; assists: number; yellowCards: number; redCards: number }[];
    standings: { clubId: string; position: number }[];
  } | null;
  standingsPrefill: StandingsPrefill;
  goalsPrefill: { goals: number | null; source: GoalsPrefillSource; mismatch: boolean };
  proposal: GameweekFetchProposal | null;
}

export type GoalsPrefillSource = "FIXTURES" | "PROPOSAL" | "NONE";

export function fetchGameweekOps(user: User, competitionId: string, gwId: string): Promise<GameweekOpsData> {
  return apiFetch(user, `/admin/competitions/${competitionId}/gameweeks/${gwId}`);
}

export interface GameweekImpact {
  playerStatRows: number;
  standingRows: number;
  leagues: { id: string; name: string; mode: string; memberCount: number; currentStatus: string }[];
  leaguesThatWillComplete: number;
  leaguesThatWillReopen: number;
  isFinalGameweek: boolean;
  gwChallengeLocksAffected: boolean;
  fpLocksAffected: number;
}

export function fetchGameweekImpact(user: User, competitionId: string, gwId: string): Promise<GameweekImpact> {
  return apiFetch(user, `/admin/competitions/${competitionId}/gameweeks/${gwId}/impact`);
}

export interface AdminFixture {
  id: string;
  homeClub: { id: string; name: string };
  awayClub: { id: string; name: string };
  kickoffTime: string | null;
  homeScore: number | null;
  awayScore: number | null;
  status: string;
}

export function fetchAdminGameweekFixtures(user: User, competitionId: string, gwId: string): Promise<{ fixtures: AdminFixture[] }> {
  return apiFetch(user, `/admin/competitions/${competitionId}/gameweeks/${gwId}/fixtures`);
}

export function createAdminFixture(
  user: User,
  competitionId: string,
  gwId: string,
  homeClubId: string,
  awayClubId: string,
  homeScore: number | null,
  awayScore: number | null,
): Promise<AdminFixture> {
  return apiFetch(user, `/admin/competitions/${competitionId}/gameweeks/${gwId}/fixtures`, {
    method: "POST",
    body: JSON.stringify({
      homeClubId,
      awayClubId,
      homeScore,
      awayScore,
      status: homeScore !== null ? "FINISHED" : "SCHEDULED",
      decidedBy: homeScore !== null ? "NORMAL_TIME" : null,
    }),
  });
}

export function patchAdminFixture(
  user: User,
  competitionId: string,
  gwId: string,
  fixtureId: string,
  data: { status?: string; homeScore?: number | null; awayScore?: number | null },
): Promise<AdminFixture> {
  return apiFetch(user, `/admin/competitions/${competitionId}/gameweeks/${gwId}/fixtures/${fixtureId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteAdminFixture(user: User, competitionId: string, gwId: string, fixtureId: string): Promise<void> {
  return apiFetch(user, `/admin/competitions/${competitionId}/gameweeks/${gwId}/fixtures/${fixtureId}`, { method: "DELETE" });
}

export type StandingsPrefillSource = "DERIVED" | "PREVIOUS" | "NONE";

/** Mirrors backend/src/lib/leagueTable.ts's LeagueTableRow — the numbers a derived position was
 * computed from, so the admin UI can show a real table instead of a bare club list and flag
 * clubs level on points/GD/goals-for as a genuine (and arbitrarily-ordered) tie. */
export interface LeagueTableRow {
  clubId: string;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  points: number;
}

export interface StandingsPrefill {
  standings: { clubId: string; position: number }[];
  source: StandingsPrefillSource;
  table: LeagueTableRow[] | null;
}

export function fetchStandingsPrefill(
  user: User,
  competitionId: string,
  gwId: string,
): Promise<StandingsPrefill> {
  return apiFetch(user, `/admin/competitions/${competitionId}/gameweeks/${gwId}/standings-prefill`);
}

// ─── Users ────────────────────────────────────────────────────────────────────

export interface AdminUser {
  id: string;
  displayName: string;
  email: string;
  role: "USER" | "ADMIN";
  banned: boolean;
  createdAt: string;
  adminOf: { id: string; name: string }[];
}

export interface AdminUsersPage {
  users: AdminUser[];
  total: number;
  page: number;
  pageSize: number;
}

export function fetchAdminUsers(user: User, params?: { search?: string; page?: number; sortBy?: string; sortDir?: "asc" | "desc" }): Promise<AdminUsersPage> {
  const q = new URLSearchParams();
  if (params?.search) q.set("search", params.search);
  if (params?.page) q.set("page", String(params.page));
  if (params?.sortBy) q.set("sortBy", params.sortBy);
  if (params?.sortDir) q.set("sortDir", params.sortDir);
  return apiFetch(user, `/admin/users?${q}`);
}

export function banUser(user: User, userId: string, opts?: { removeFromActiveLeagues?: boolean; removeFromAllLeagues?: boolean }): Promise<AdminUser> {
  return apiFetch(user, `/admin/users/${userId}/ban`, {
    method: "PATCH",
    body: JSON.stringify(opts ?? {}),
  });
}

export function unbanUser(user: User, userId: string): Promise<AdminUser> {
  return apiFetch(user, `/admin/users/${userId}/unban`, { method: "PATCH", body: JSON.stringify({}) });
}

export interface UserDeletionImpact {
  adminOf: { id: string; name: string; memberCount: number }[];
  memberships: { leagueMemberId: string; leagueId: string; leagueName: string }[];
}

export function fetchUserDeletionImpact(user: User, userId: string): Promise<UserDeletionImpact> {
  return apiFetch(user, `/admin/users/${userId}/deletion-impact`);
}

export function deleteUser(user: User, userId: string): Promise<void> {
  return apiFetch(user, `/admin/users/${userId}`, { method: "DELETE" });
}

// ─── Leagues ─────────────────────────────────────────────────────────────────

export interface AdminLeague {
  id: string;
  name: string;
  admin: { displayName: string };
  competition: { name: string; season: string };
  _count: { members: number };
}

export interface AdminLeaguesPage {
  leagues: AdminLeague[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminLeagueDetail {
  id: string;
  name: string;
  adminId: string;
  mode: LeagueMode;
  competitionId: string;
  // Derived on the backend (getLeagueStatus), not a stored column — tells the admin UI
  // whether adding a member is a plain add (PREPARING), requires backfilling their
  // selections (RUNNING, TARGET/FINAL_POSITIONS/SYNDICATE only), or is blocked (COMPLETED).
  status: LeagueStatus;
  admin: { id: string; displayName: string };
  competition: { name: string; season: string };
  members: {
    id: string;
    status: string;
    user: { id: string; displayName: string; email: string } | null;
    guestDisplayName: string | null;
  }[];
}

/** Payload for backfilling a TARGET/FINAL_POSITIONS/SYNDICATE member's selections in the
 * same call as adding them — required once AdminLeagueDetail.status is "RUNNING" for those
 * three modes; ignored (and rejected) for GAMEWEEK_CHALLENGE/SCORE_PREDICTOR. Also reused,
 * unchanged, as the body shape for `adminUpdateMemberSelections` below — the `gwcPicks` arm
 * only applies there (the add-member route never accepts it; GWC never needs backfilling). */
export type AdminMemberSelections =
  | { playerIds: string[] }
  | { predictions: { clubId: string; position: number }[] }
  | { picks: { potId: string; clubId?: string | null; playerId?: string | null }[]; tiebreaker: number }
  | { gwcPicks: { gameweekId: string; playerId: string }[] };

/** GET .../selections response — mode-tagged, and carrying display metadata (names, club
 * names) beyond raw ids because the edit pickers render names, not ids. */
export type AdminMemberSelectionsDetail =
  | { mode: "TARGET"; playerIds: string[]; players: { id: string; name: string; position: string | null; club: string | null }[] }
  | { mode: "FINAL_POSITIONS"; predictions: { clubId: string; position: number }[] }
  | {
      mode: "SYNDICATE";
      picks: { potId: string; clubId: string | null; playerId: string | null; playerName: string | null; playerClub: string | null; playerPosition: string | null }[];
      tiebreaker?: number;
    }
  | {
      mode: "GAMEWEEK_CHALLENGE";
      gameweeks: { id: string; number: number; completed: boolean }[];
      picks: { gameweekId: string; playerId: string; playerName: string | null; playerClub: string | null }[];
    };

export function fetchAdminLeagues(user: User, params?: { search?: string; page?: number; sortBy?: string; sortDir?: "asc" | "desc" }): Promise<AdminLeaguesPage> {
  const q = new URLSearchParams();
  if (params?.search) q.set("search", params.search);
  if (params?.page) q.set("page", String(params.page));
  if (params?.sortBy) q.set("sortBy", params.sortBy);
  if (params?.sortDir) q.set("sortDir", params.sortDir);
  return apiFetch(user, `/admin/leagues?${q}`);
}

export function fetchAdminLeague(user: User, leagueId: string): Promise<AdminLeagueDetail> {
  return apiFetch(user, `/admin/leagues/${leagueId}`);
}

export function reassignLeagueAdmin(user: User, leagueId: string, newAdminUserId: string): Promise<AdminLeague> {
  return apiFetch(user, `/admin/leagues/${leagueId}/admin`, {
    method: "PATCH",
    body: JSON.stringify({ newAdminUserId }),
  });
}

export function adminDeleteLeague(user: User, leagueId: string): Promise<void> {
  return apiFetch(user, `/admin/leagues/${leagueId}`, { method: "DELETE" });
}

export function adminRemoveMember(user: User, leagueId: string, memberId: string): Promise<void> {
  return apiFetch(user, `/admin/leagues/${leagueId}/members/${memberId}`, { method: "DELETE" });
}

export function adminAddMember(
  user: User,
  leagueId: string,
  email: string,
  selections?: AdminMemberSelections,
): Promise<{ id: string; displayName: string }> {
  return apiFetch(user, `/admin/leagues/${leagueId}/members`, {
    method: "POST",
    body: JSON.stringify(selections ? { email, selections } : { email }),
  });
}

/** Mid-season correction: lets a site admin view and overwrite an existing approved
 * member's picks (guest or registered) for TARGET / FINAL_POSITIONS / SYNDICATE /
 * GAMEWEEK_CHALLENGE — deliberately not gated by league status; see the backend route's
 * own comment for why. SCORE_PREDICTOR is not supported here. */
export function fetchAdminMemberSelections(user: User, leagueId: string, memberId: string): Promise<AdminMemberSelectionsDetail> {
  return apiFetch(user, `/admin/leagues/${leagueId}/members/${memberId}/selections`);
}

export function adminUpdateMemberSelections(
  user: User,
  leagueId: string,
  memberId: string,
  selections: AdminMemberSelections,
): Promise<{ saved: number | boolean }> {
  return apiFetch(user, `/admin/leagues/${leagueId}/members/${memberId}/selections`, {
    method: "PUT",
    body: JSON.stringify(selections),
  });
}

// ─── Player search (all players, no competition filter) ───────────────────────
export function adminSearchPlayers(user: User, q: string): Promise<{ players: { id: string; name: string; club: string }[] }> {
  return apiFetch(user, `/admin/players/search?q=${encodeURIComponent(q)}`);
}

// ─── Competition Players / Squad ──────────────────────────────────────────────

export interface CompetitionPlayer {
  playerId: string;
  name: string;
  nationality: string | null;
  position: string | null;
  squadNumber: number | null;
  club: { id: string; name: string };
  active: boolean;
  transferredOutAt: string | null;
}

export interface CompetitionPlayersPage {
  players: CompetitionPlayer[];
  total: number;
  page: number;
  pageSize: number;
}

export function fetchCompetitionPlayers(
  user: User,
  competitionId: string,
  params?: { clubId?: string; search?: string; active?: boolean; page?: number },
): Promise<CompetitionPlayersPage> {
  const q = new URLSearchParams();
  if (params?.clubId) q.set("clubId", params.clubId);
  if (params?.search) q.set("search", params.search);
  if (params?.active !== undefined) q.set("active", String(params.active));
  if (params?.page) q.set("page", String(params.page));
  return apiFetch(user, `/admin/competitions/${competitionId}/players?${q}`);
}

export function addPlayerToCompetition(
  user: User,
  competitionId: string,
  payload: { playerId?: string; clubId: string; name?: string; nationality?: string; position?: string; squadNumber?: number },
): Promise<CompetitionPlayer> {
  return apiFetch(user, `/admin/competitions/${competitionId}/players`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateCompetitionPlayer(
  user: User,
  competitionId: string,
  playerId: string,
  payload: { clubId?: string; active?: boolean },
): Promise<CompetitionPlayer> {
  return apiFetch(user, `/admin/competitions/${competitionId}/players/${playerId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function removeCompetitionPlayer(user: User, competitionId: string, playerId: string): Promise<void> {
  return apiFetch(user, `/admin/competitions/${competitionId}/players/${playerId}`, { method: "DELETE" });
}

export interface PlayerUsage {
  selections: number;
  stats: number;
  gwPicks: number;
  syndicatePicks: number;
  total: number;
}

export function fetchPlayerUsage(user: User, competitionId: string, playerId: string): Promise<PlayerUsage> {
  return apiFetch(user, `/admin/competitions/${competitionId}/players/${playerId}/usage`);
}

// ─── Set guest member predictions ─────────────────────────────────────────────
export function adminSetGuestPredictions(
  user: User,
  leagueId: string,
  memberId: string,
  predictions: { clubId: string; position: number }[],
): Promise<{ saved: number }> {
  return apiFetch(user, `/admin/leagues/${leagueId}/members/${memberId}/predictions`, {
    method: "POST",
    body: JSON.stringify({ predictions }),
  });
}

// ─── Metrics ──────────────────────────────────────────────────────────────────

export interface WeeklyBucket { weekStart: string; count: number }

export interface SiteMetrics {
  users: { total: number; verified: number; unverified: number; registrationCap: number; registrationCapRemaining: number };
  leagues: {
    total: number; running: number; completed: number;
    byMode: { mode: string; count: number }[];
    byCompetition: { competition: string; count: number }[];
  };
  members: { total: number; guests: number };
  waitlist: { pending: number };
  trends: { weeklySignups: WeeklyBucket[]; weeklyLeagues: WeeklyBucket[] };
}

export function fetchMetrics(user: User): Promise<SiteMetrics> {
  return apiFetch(user, "/admin/metrics");
}

export interface FunnelSourceRow {
  source: string;
  signups: number;
  createdLeaguePct: number;
  joinedLeaguePct: number;
  submittedPickPct: number;
  medianHoursToFirstLeague: number | null;
}

export interface ReferralRow {
  referrerUserId: string;
  referrerEmail: string | null;
  referrerDisplayName: string | null;
  totalReferred: number;
  activatedReferred: number;
}

export interface Funnel {
  bySource: FunnelSourceRow[];
  referralBreakdown: ReferralRow[];
}

export function fetchFunnel(user: User): Promise<Funnel> {
  return apiFetch(user, "/admin/funnel");
}

// ─── Syndicate pots ───────────────────────────────────────────────────────────

export type SyndicatePotKind = "CLUB" | "PLAYER";
export type SyndicatePotStatType = "GOALS" | "ASSISTS" | "YELLOW_CARDS" | "RED_CARDS";

export interface AdminSyndicatePot {
  id: string;
  order: number;
  kind: SyndicatePotKind;
  label: string;
  winPoints: number | null;
  drawPoints: number | null;
  statType: SyndicatePotStatType | null;
  pointsPer: number | null;
  clubs: { id: string; name: string; shortName: string | null }[];
  excludedPlayerIds: string[];
  excludedPlayers: { id: string; name: string; club: string }[];
}

export interface AdminSyndicatePotsResponse {
  frozen: boolean;
  pots: AdminSyndicatePot[];
}

/** A pot as sent to PUT — `id` omitted creates a new pot. Array position (not a
 * client order field) sets display order. Scoring-affecting fields are ignored
 * server-side once `frozen` — only `label` moves. */
export interface AdminSyndicatePotInput {
  id?: string;
  label: string;
  kind: SyndicatePotKind;
  winPoints?: number;
  drawPoints?: number;
  clubIds?: string[];
  statType?: SyndicatePotStatType;
  pointsPer?: number;
  excludedPlayerIds?: string[];
}

export function fetchSyndicatePots(user: User, competitionId: string): Promise<AdminSyndicatePotsResponse> {
  return apiFetch(user, `/admin/competitions/${competitionId}/syndicate-pots`);
}

export function saveSyndicatePots(
  user: User,
  competitionId: string,
  pots: AdminSyndicatePotInput[],
  deletePotIds?: string[],
): Promise<{ saved: boolean; frozen: boolean; invalidatedPickCount: number; deletedPickCount: number }> {
  return apiFetch(user, `/admin/competitions/${competitionId}/syndicate-pots`, {
    method: "PUT",
    body: JSON.stringify({ pots, ...(deletePotIds ? { deletePotIds } : {}) }),
  });
}
